import {
  Button,
  Typography,
  Input,
  ErrorAlert,
  BooleanCheckbox,
  PasswordInput,
} from "@sgi/gravity";
import { Box, Flex, Spacer, Stack, Center, useBreakpoint, StyleProps } from "@chakra-ui/react";
import {ChangeEvent, FC, useCallback, useState} from "react";
import "./signIn.css";
import { useWrapper } from "../Context/WrapperContext";
import ButtonLink from "../Components/ButtonLink";
import { useUnifiedLayoutProxy } from "../Custom Hooks/useUnifiedLayoutProxy";
import { useResponsiveWidth } from "../Custom Hooks/useResponsiveWidth"; 

export default function SignIn() {
  const SIGN_IN_WIDTH = useResponsiveWidth();
  const { increment } = useWrapper();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const {
    signInAction, navigateToForgotPasswordAction,
    signInError, emailError, passwordError,
  } = useUnifiedLayoutProxy({ email, password, rememberMe });
  const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value), []);
  const handlePasswordChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value), []);
  const navigateToCreateAccount = useCallback(() => {
    window.location.href = window.location.href.replace(/_SIGNIN([_&/])/i, (_, suffix) => `_signup${suffix}`);
  }, []);

  return (
    <Box
      style={{
        position: "relative",
        minWidth: "100vw",
      }}
      data-testid="signin-test"
      overflow="hidden"
    >
      <Flex
        width={"100vw"}
        height={"100vh"}
        alignContent={"center"}
        justifyContent={"center"}
      >
        <Center>
          <Flex
            direction="column"
            width={SIGN_IN_WIDTH}
            maxW={740}
            borderWidth="1px"
            borderRadius="lg"
            style={{
              padding: "50px",
              position: "relative",
            }}
            borderColor="rgba(0, 0, 0, 0.16)"
            background="white"
          >
            <Typography variant="h2">Sign in</Typography>
            <Typography variant="body" mb={8}>
              to your MySGI account
            </Typography>
            <Box data-testid="error-container-test" mb={8}>
              {signInError && <SignInAlert errorMessage={signInError} />}
            </Box>
            <Input
              name="email"
              placeholder="Enter email"
              marginBottom="3"
              label={
                <Flex>
                  <Typography variant="bodyBold" color="gray.800">
                    Email
                  </Typography>
                  <Spacer />
                  <Typography className="custom-link" color="teal.500">
                    <ButtonLink
                      testid="forgot-email-test"
                      linkCaption="Forgot Email?"
                      handleClick={increment}
                    />
                  </Typography>
                </Flex>
              }
              tabIndex={1}
              type="text"
              data-testid="email-test"
              autoComplete="email"
              isRequired={true}
              value={email}
              onChange={handleEmailChange}
              error={emailError}
            />
            <PasswordInput
              name="password"
              placeholder="Enter password"
              label={
                <Flex>
                  <Typography variant="bodyBold" color="gray.800">
                    Password
                  </Typography>
                  <Spacer />
                  <ButtonLink
                    testid="forgot-password-test"
                    linkCaption="Forgot Password?"
                    handleClick={navigateToForgotPasswordAction.act}
                  />
                </Flex>
              }
              tabIndex={2}
              data-testid="password-test"
              isRequired={true}
              value={password}
              onChange={handlePasswordChange}
              error={passwordError}
            />
            <BooleanCheckbox
              name="remember-me"
              data-testid="remember-me-test"
              value={rememberMe}
              onChange={setRememberMe}
              marginTop="10px"
              marginBottom="25px"
            >
              Remember me
            </BooleanCheckbox>
            <Button
              isFullWidth
              data-testid="signin-button-test"
              style={{ marginTop: "10px", marginBottom: "25px" }}
              onClick={signInAction.act}
            >
              Sign in
            </Button>
            <Center>
              <Stack spacing="10px" direction="row">
                <Typography variant="bodyBold" color="gray.800">
                  Don't have an account?
                </Typography>
                <ButtonLink
                  testid="register-here-test"
                  linkCaption="Register here"
                  handleClick={navigateToCreateAccount}
                />
              </Stack>
            </Center>
          </Flex>
        </Center>
      </Flex>
    </Box>
  );
}

const SignInAlert: FC<{ errorMessage: string }> = ({ errorMessage }) => {
  const breakpoint = useBreakpoint({ ssr: false });
  const isMobile = ["base", "sm"].includes(breakpoint);
  const descriptionStyleProps: StyleProps = {
    fontSize: `${isMobile ? "13" : "14"}px`,
  };
  const padding = `${isMobile ? "10" : "12"}px !important`;
  const alertStyleProps: StyleProps = {
    paddingTop: padding,
    paddingBottom: padding,
  };
  return (
    <ErrorAlert
      showCloseButton={false}
      descriptionStyleProps={descriptionStyleProps}
      alertStyleProps={alertStyleProps}
    >
      {errorMessage}
    </ErrorAlert>
  );
};
